<template>
	<div>
		<ListLayout
			:title="$t('settings_orgprofile_people')"
			:buttonText="$t('admin_people_add_person_title')"
			@click="changeModalState(states.ADD_PERSON)"
		>
			<template #search>
				<div class="search-and-filter">
					<Input
						:icon="SearchIcon"
						:iconProps="{ type: 'regular' }"
						v-model="query"
						:placeholder="$t('admin_people_search_action')"
						allowClear
					/>

					<Dropdown
						:placeholder="$t('admin_people_filters_dropdown_title')"
						:options="options"
						:badge="filters.length"
						:icon="FilterIcon"
						ref="filters"
						class="filters"
					>
						<template #options>
							<div
								v-if="filters.length > 0"
								class="reset-button pointer"
								@click.stop="clearFilters"
							>
								{{ $t('admin_people_reset_button') }}
							</div>

							<div
								v-for="(options, section) in optionsBySection"
								:key="section"
							>
								<div class="option">
									<div class="section-title">{{ $t(section) }}</div>
									<div class="check-items">
										<div
											v-for="option in options"
											class="check-item"
											:key="option.id"
										>
											<label :for="option.id">
												<input
													type="checkbox"
													:id="option.id"
													v-model="option.checked"
												/>
												{{ $t(option.label) }}
											</label>
										</div>
									</div>
								</div>

								<hr v-if="section !== 'other'" />
							</div>
						</template>
					</Dropdown>
				</div>
			</template>
			<template #table>
				<Table
					:columns="columns"
					:rows="people"
					clickable
					infinite
					localize
					closePopoversOnScroll
					:loadingOverlay="loading"
					:loadingMore="loadingMore"
					:defaultSort="sort"
					@load-more="loadMore"
					loadMoreTrigger="400px"
					@row-click="seeDetails"
					@sort="onNameSort"
					headerSize="small"
					height="calc(100vh - 197px)"
					ref="table"
					:rowHeight="73"
				>
					<template #cell-name="{ data }">
						<UserCell :user="data" :class="{ deactivated: !data.active }" />
					</template>

					<template #cell-last_active="{ data }">
						<div class="last-used" :class="{ deactivated: !data.active }">
							{{ activeValue(data) }}
						</div>
					</template>

					<template #cell-active="{ data }">
						<div
							class="deactivated"
							v-text="
								data.active ? '' : $t('admin_account_status_state_deactivated')
							"
						/>
					</template>

					<template #cell-actions="{ data }">
						<DotsMenu
							localize
							:items="contextMenuOptions(data)"
							@click.stop
							@select="onMenuItemClick($event, data)"
						/>
					</template>

					<template #empty>
						<NoSearchResults :query="query">
							<div
								class="deactivated-tip"
								v-if="!includesDeactivated"
								v-t="'admin_people_empty_deactivated_tip'"
							/>
						</NoSearchResults>
					</template>
				</Table>
			</template>
		</ListLayout>

		<AddPerson
			v-if="modalState === states.ADD_PERSON"
			@cancel="changeModalState(null)"
			@success="personAdded"
		/>

		<PersonAdded
			v-if="modalState === states.SHOW_PERSON"
			@addPerson="changeModalState(states.ADD_PERSON)"
			@done="changeModalState(null)"
			:user="this.userData"
		/>

		<DeactivatePersonModal
			v-if="showDeactivate"
			@close="showDeactivate = false"
			:person="contextUser"
			@deactivate="deactivateUser"
		/>

		<ReactivatePersonModal
			v-if="showReactivate"
			@close="showReactivate = false"
			:person="contextUser"
			@reactivate="reactivateUser"
		/>

		<SendWelcomeModal
			v-if="showSendWelcome"
			@close="showSendWelcome = false"
			:user="contextUser"
		/>
	</div>
</template>

<script>
import { markRaw } from 'vue'
import AddPerson from './AddPerson'
import PersonAdded from './PersonAdded'
import DeactivatePersonModal from './DeactivatePersonModal'
import ReactivatePersonModal from './ReactivatePersonModal'
import {
	Table,
	Input,
	Dropdown,
	DotsMenu,
	closeAllMenus,
	ASC,
} from '@/components/common'
import { SearchIcon, FilterIcon } from '@/icons'
import { UserCell, ListLayout, NoSearchResults, get } from '../common'
import { getTimeAgoShorthand, storage } from '@/helpers/utils'
import debounce from 'debounce'
import SendWelcomeModal from './SendWelcomeModal'

const states = {
	ADD_PERSON: 1,
	SHOW_PERSON: 2,
}

const PAGE_SIZE = 50

export default {
	name: 'People',
	components: {
		AddPerson,
		PersonAdded,
		DeactivatePersonModal,
		ReactivatePersonModal,
		Table,
		Input,
		Dropdown,
		UserCell,
		SendWelcomeModal,
		DotsMenu,
		ListLayout,
		NoSearchResults,
	},
	props: {
		searchTerm: String,
	},

	data() {
		return {
			contextUser: null,
			people: [],
			states,
			SearchIcon: markRaw(SearchIcon),
			FilterIcon: markRaw(FilterIcon),
			loading: true,
			loadingMore: false,
			fetchError: false,
			query: this.searchTerm || null,
			modalState: null,
			showDeactivate: false,
			showReactivate: false,
			showSendWelcome: false,
			sort: { field: 'name', param: 'firstname,lastname', order: ASC },
			userData: {},
			options: [
				{
					id: 'admin',
					label: 'model_role_admin',
					section: 'admin_people_filter_role_label',
					checked: false,
					kind: 'role',
				},
				{
					id: 'manager',
					label: 'model_role_manager',
					section: 'admin_people_filter_role_label',
					checked: false,
					kind: 'role',
				},
				{
					id: 'user',
					label: 'model_role_user',
					section: 'admin_people_filter_role_label',
					checked: false,
					kind: 'role',
				},
				{
					id: 'deactivated',
					label: 'admin_account_status_state_deactivated_include',
					section: 'admin_people_filter_account_status_label',
					checked: false,
					kind: 'active',
				},
				{
					id: 'not_signed_in',
					label: 'admin_people_filter_have_not_signed_in_label',
					section: 'admin_people_filter_other_label',
					checked: false,
					kind: 'presence',
				},
			],
		}
	},

	beforeRouteLeave(to, from, next) {
		this.modalState = null
		next()
	},

	created() {
		this.fetchUsersDebounced = debounce(this.fetchUsers.bind(this), 500)
	},

	async mounted() {
		await this.fetchUsers()
	},

	async activated() {
		// if some user was modified on another screen, refetch the list
		if (storage.get('org_mgmt_changed_people')) {
			storage.remove('org_mgmt_changed_people')
			await this.fetchUsers()
		}
	},

	watch: {
		showDeactivate: function (val, oldVal) {
			if (oldVal && !val) {
				this.contextUser = null
			}
		},
		query() {
			this.loading = true
			this.fetchUsersDebounced()
		},
		filters: {
			handler() {
				this.fetchUsersDebounced()
			},
			deep: true,
		},
	},

	computed: {
		optionsBySection() {
			return this.options.reduce((acc, option) => {
				acc[option.section] = acc[option.section] || []
				acc[option.section].push(option)
				return acc
			}, {})
		},

		filters() {
			return this.options.filter((option) => option.checked)
		},

		includesDeactivated() {
			return this.filters.some((f) => f.id === 'deactivated')
		},

		columns() {
			return [
				{
					field: 'name',
					label: this.$t('admin_people_name_label'),
					width: '100%',
					minWidth: '200px',
					sorter: true,
					ascSort: 'firstname,lastname',
					dscSort: '-firstname,-lastname',
				},
				{
					field: 'last_active',
					label: this.$t('admin_people_last_active_label'),
				},
				this.includesDeactivated && {
					field: 'active',
					label: this.$t('admin_people_filter_account_status_label'),
				},
				{
					field: 'actions',
					minWidth: '100px',
					align: 'center',
				},
			].filter((x) => x)
		},
	},

	methods: {
		addPerson() {
			// reset any previous state
			// open appropriate modal
			this.changeModalState(states.ADD_PERSON)
			this.user = {}
		},

		deactivateUser(id) {
			this.people.find((x) => x.id === id).active = false
			this.showDeactivate = false
		},

		reactivateUser(id) {
			this.people.find((x) => x.id === id).active = true
			this.showReactivate = false
		},

		lastActive(person) {
			return person.lastpresencetime
				? this.timeFromNow(person.lastpresencetime)
				: this.$t('people_presence_active_never_short')
		},

		activeValue(person) {
			return person.isonline
				? this.$t('people_presence_active_now')
				: this.lastActive(person)
		},

		async fetchUsers() {
			this.loading = true
			this.fetchError = false

			try {
				const params = {
					'page[size]': PAGE_SIZE,
					'filter[active]': [true],
				}

				if (this.sort) {
					params['sort'] = this.sort.param
				}

				if (this.query) {
					params['filter[search]'] = encodeURIComponent(this.query)
				}

				// check for a presence filter
				const presence = this.filters.find((f) => f.kind === 'presence')
				if (presence) {
					params['filter[presence]'] = false
				}
				// check for activity filter
				const deactivated = this.filters.find((f) => f.id === 'deactivated')
				if (deactivated) {
					// we should fetch both active and deactivated, which is the api default
					delete params['filter[active]']
				}
				// check for a role filter
				const role = this.filters.filter((f) => f.kind === 'role')
				if (role.length) {
					params['filter[role]'] = role.map((f) => f.id)
				}

				this.apiResponse = await get('/organization/users', params)
				this.people = this.apiResponse.data
				this.loading = false
				this.$refs.table?.scrollToTop()
			} catch (e) {
				console.error(e)
				this.loading = false
				this.fetchError = true
				this.$alert.error(this.$t('common_api_error_save'))
			}
		},

		async loadMore() {
			if (!this.apiResponse?.next) return
			try {
				this.loadingMore = true
				this.apiResponse = await this.apiResponse.next()
				this.people = this.people.concat(this.apiResponse.data)
			} catch (e) {
				console.error(e)
				this.$alert.error(this.$t('common_api_error_save'))
			}
			this.loadingMore = false
		},

		onNameSort(sort, { ascSort, dscSort }) {
			this.sort = {
				...sort,
				param: sort.order === ASC ? ascSort : dscSort,
			}
			this.fetchUsers()
		},

		timeFromNow(dateStr) {
			const time = getTimeAgoShorthand(dateStr)
			return this.$t('common_date_relative', [time])
		},

		personAdded(user) {
			// show the PersonAdded modal
			this.changeModalState(states.SHOW_PERSON)
			this.userData = user
			// refetch the current list of users in case new user should appear
			this.fetchUsers()
		},

		changeModalState(state) {
			this.modalState = state
		},

		clearFilters() {
			this.options = this.options.map((option) => {
				option.checked = false
				return option
			})
		},

		onMenuItemClick({ id }, user) {
			switch (id) {
				case 'welcome':
					this.contextUser = user
					return (this.showSendWelcome = true)
				case 'activation':
					this.contextUser = user
					if (user.active) {
						return (this.showDeactivate = true)
					} else {
						return (this.showReactivate = true)
					}
			}
			this.contextUser = null
		},

		seeDetails({ id }) {
			closeAllMenus()
			this.$router.push(`/organization/users/${id}`)
		},

		contextMenuOptions(user) {
			const send_welcome = {
				id: 'welcome',
				label: 'admin_people_send_welcome_message_label',
			}

			const labels = [
				{
					id: 'activation',
					label: user.active
						? 'admin_people_deactivate_button'
						: 'admin_people_reactivate_button',
					danger: user.active,
				},
			]

			if (user.active) labels.unshift(send_welcome)

			return labels
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.deactivated {
	opacity: 40%;
}

.search-and-filter {
	display: flex;
	:deep(.pronto-input) {
		max-width: 500px;
		margin-right: 16px;
	}

	:deep(svg) path {
		fill: var(--neutral-400);
	}
}

:deep(.dropdown) {
	flex: 1;

	.dropdown-selected {
		.dropdown-selected-label {
			padding-left: 5px;
		}
	}

	.dropdown-options-container {
		max-width: 380px;
		width: 380px;
		transition-property: max-height, border-color, background, opacity;
		opacity: 0;
		&.open {
			opacity: 1;
		}
	}

	.pronto-icon {
		align-self: center;
	}
}

.filters {
	.check-items {
		display: flex;
	}

	.check-item {
		margin-right: 24px;
		label {
			font-family: Avenir-Roman;
			font-size: 16px;
			color: #4f5053;
			cursor: pointer;
		}
	}

	hr {
		border-color: #eaeaea;
		border-top: unset;
	}

	:deep(.dropdown-container) {
		max-width: 220px;
		.dropdown-options-container {
			&.scrollable {
				overflow: hidden;
			}

			&.open {
				max-height: 315px;
				height: 315px;
			}

			.dropdown-options {
				padding: unset;
			}

			.scroller {
				max-height: 315px;
				overflow: hidden;
			}
		}
	}

	.option {
		margin: 24px 16px;

		.section-title {
			font-family: Avenir-Heavy;
			font-size: 16px;
			color: #4f5053;
			margin-bottom: 11px;
		}
	}
}

.reset-button {
	position: absolute;
	top: -16px;
	right: 16px;
	color: var(--blue-300);
	font-family: Avenir-Heavy;
	font-size: 14px;
}

.actions {
	cursor: pointer;
	padding: 4px 16px;
}

.actions-section {
	display: flex;
	align-items: center;
}

.no-search-results {
	@include flex-column-center;
	font-size: 24px;
	color: var(--neutral-800);
	.pronto-icon {
		font-size: 75px;
		color: var(--blue-300);
		margin-bottom: 24px;
	}
}

.deactivated-tip {
	font-size: 16px;
	margin-top: 16px;
}
</style>
